/**
 * Security utilities for input sanitization and validation
 * 
 * IMPORTANT: Always sanitize user input before:
 * - Sending to the server
 * - Rendering in the DOM
 * - Using in URLs or queries
 */

/**
 * Escapes HTML special characters to prevent XSS attacks
 */
export function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Sanitizes user input before sending to the server
 * - Removes control characters (except allowed whitespace)
 * - Normalizes multiple spaces
 * - Trims whitespace
 */
export function sanitizeInput(
  input: string,
  options?: { maxLength?: number },
): string {
  if (typeof input !== 'string') {
    return '';
  }

  const normalized = input
    // Remove control characters (except \n, \r, \t)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // Normalize multiple spaces to single space
    .replace(/[^\S\n\r]+/g, ' ')
    // Trim leading/trailing whitespace
    .trim();

  return options?.maxLength !== undefined
    ? normalized.slice(0, options.maxLength)
    : normalized;
}

/**
 * Sanitizes a string for use in URLs
 */
export function sanitizeUrlParam(param: string): string {
  return encodeURIComponent(sanitizeInput(param));
}

/**
 * Sanitizes a URL for safe use (e.g., href/src attributes).
 *
 * Only `http:`/`https:` URLs are allowed; `javascript:`, `data:` and any
 * other scheme (case-insensitive) are rejected with an empty string.
 */
export function sanitizeUrl(url: string): string {
  const trimmed = sanitizeInput(url);
  if (!trimmed) return '';
  try {
    const { protocol } = new URL(trimmed);
    return protocol === 'http:' || protocol === 'https:' ? trimmed : '';
  } catch {
    return '';
  }
}

/**
 * Recursively sanitizes all string values in an object
 * Use before sending form data to the API
 */
export function sanitizePayload<T extends Record<string, unknown>>(obj: T): T {
  const sanitized = {} as T;
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key as keyof T] = sanitizeInput(value) as T[keyof T];
    } else if (Array.isArray(value)) {
      sanitized[key as keyof T] = value.map(item => 
        typeof item === 'string' 
          ? sanitizeInput(item) 
          : typeof item === 'object' && item !== null
            ? sanitizePayload(item as Record<string, unknown>)
            : item
      ) as T[keyof T];
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key as keyof T] = sanitizePayload(value as Record<string, unknown>) as T[keyof T];
    } else {
      sanitized[key as keyof T] = value as T[keyof T];
    }
  }
  
  return sanitized;
}

/**
 * Validates that a string matches an expected pattern
 */
export function validatePattern(input: string, pattern: RegExp): boolean {
  return pattern.test(input);
}

/**
 * Common validation patterns
 */
export const PATTERNS = {
  // Alphanumeric with spaces, hyphens, underscores
  SAFE_NAME: /^[\w\s\-]+$/,
  // Slug format: lowercase, numbers, hyphens
  SLUG: /^[a-z0-9\-]+$/,
  // Email (basic validation)
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // UUID v4
  UUID: /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  // No special characters that could be used for injection
  NO_INJECTION: /^[^<>'"`;(){}[\]\\]*$/,
} as const;

/**
 * Checks if a filename is safe (no path traversal)
 */
export function isSafeFilename(filename: string): boolean {
  // No path separators
  if (filename.includes('/') || filename.includes('\\')) {
    return false;
  }
  // No parent directory references
  if (filename.includes('..')) {
    return false;
  }
  // No hidden files (starting with .)
  if (filename.startsWith('.')) {
    return false;
  }
  // Reasonable length
  if (filename.length > 255 || filename.length === 0) {
    return false;
  }
  return true;
}

/**
 * Sanitizes a filename for safe storage
 */
export function sanitizeFilename(filename: string): string {
  // Get just the filename (no path)
  const name = filename.split(/[/\\]/).pop() || 'unnamed';
  
  // Replace dangerous characters
  const safeName = name
    .replace(/[^\w.\-]/g, '_')
    .replace(/^\.+/, '') // Remove leading dots
    .slice(0, 255); // Limit length
  
  return safeName || 'unnamed_file';
}

/**
 * Rate limiting helper for client-side protection
 */
export class RateLimiter {
  private timestamps: number[] = [];
  
  constructor(
    private readonly maxRequests: number,
    private readonly windowMs: number
  ) {}
  
  canProceed(): boolean {
    const now = Date.now();
    // Remove old timestamps outside the window
    this.timestamps = this.timestamps.filter(t => now - t < this.windowMs);
    
    if (this.timestamps.length >= this.maxRequests) {
      return false;
    }
    
    this.timestamps.push(now);
    return true;
  }
  
  reset(): void {
    this.timestamps = [];
  }
}
